import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';
import { PageHeader } from '../components/Layout';
import { Button, Card, Grid, KV, Metric, Pill, SectionHeader } from '../components/ui';
import { money, num, pct, relativeTime } from '../lib/format';
import type { MetricsPayload } from '../lib/metrics';
import {
  buildExecutiveSnapshot,
  type AgentSeverity,
  type ExecutiveAgentBrief,
} from '../lib/executiveSnapshot';
import { COLORS, RADIUS, healthColor } from '../theme';

export default function DashboardPage() {
  const [metrics, setMetrics] = useState<MetricsPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    const { data, error } = await supabase.functions.invoke<MetricsPayload>('admin-metrics', { body: {} });
    if (error || !data) {
      setError(error?.message ?? 'Failed to load metrics.');
    } else {
      setMetrics(data);
    }
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  const briefs = useMemo<ExecutiveAgentBrief[]>(
    () => (metrics ? buildExecutiveSnapshot(metrics) : []),
    [metrics],
  );

  if (loading) return <div style={loadingStyle}>Loading metrics…</div>;
  if (error)   return <div style={errorStyle}>{error} <Button onClick={() => { setLoading(true); void load(); }}>Retry</Button></div>;
  if (!metrics) return null;

  const ex = metrics.executive;
  return (
    <div>
      <PageHeader
        title="Dashboard"
        right={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ color: COLORS.textMuted, fontSize: 12 }}>
              As of {new Date(metrics.asOf).toLocaleString()}
            </span>
            <Button variant="ghost" onClick={() => { setLoading(true); void load(); }}>Refresh</Button>
          </div>
        }
      />

      <SectionExecutiveSnapshot briefs={briefs} />

      <Card title="Health">
        <Grid cols={4}>
          <Metric label="Overall"      value={ex.overallHealth}      sub="0–100" color={healthColor(ex.overallHealth)} />
          <Metric label="Reliability"  value={ex.reliabilityScore}   sub="0–100" color={healthColor(ex.reliabilityScore)} />
          <Metric label="Profitability" value={ex.profitabilityScore} sub="0–100" color={healthColor(ex.profitabilityScore)} />
          <Metric label="System"       value={ex.systemHealth}       sub="0–100" color={healthColor(ex.systemHealth)} />
        </Grid>
      </Card>

      <SectionHeader>Executive</SectionHeader>
      <Grid cols={2}>
        <Card>
          <Grid cols={2}>
            <Metric label="Revenue today"           value={money(ex.todayRevenueUsd)} />
            <Metric label="Estimated profit today"  value={money(ex.todayEstimatedProfitUsd)} />
            <Metric label="AI cost today"           value={money(ex.todayAiCostUsd)} />
            <Metric label="Gross margin"            value={pct(ex.grossMarginPct)} />
          </Grid>
        </Card>
        <Card>
          <Grid cols={2}>
            <Metric label="Active businesses"       value={num(ex.todayActiveBusinesses)} />
            <Metric label="Google accounts"         value={num(ex.connectedGoogleAccounts)} />
            <Metric label="MRR"                     value={money(ex.monthlyRunRateUsd)} />
            <Metric label="Projected ARR"           value={money(ex.projectedArrUsd)} />
          </Grid>
        </Card>
      </Grid>

      <SectionHeader>Revenue</SectionHeader>
      <Card>
        <Grid cols={4}>
          <Metric label="Free"     value={num(metrics.revenue.counts.free)} />
          <Metric label="Core"     value={num(metrics.revenue.counts.core)} />
          <Metric label="Pro"      value={num(metrics.revenue.counts.pro)} />
          <Metric label="Lifetime" value={num(metrics.revenue.counts.lifetime)} />
        </Grid>
      </Card>

      <SectionHeader>Customer Health</SectionHeader>
      <CustomerHealth data={metrics.customerHealth} />

      <SectionHeader>Release Health</SectionHeader>
      <ReleaseHealth data={metrics.releaseHealth} />

      <SectionHeader>Localization</SectionHeader>
      <Localization data={metrics.localization} />

      <SectionHeader>Crash Center</SectionHeader>
      <CrashCenter data={metrics.crashCenter} />
    </div>
  );
}

function CustomerHealth({ data }: { data: MetricsPayload['customerHealth'] }) {
  const funnel = data.onboardingFunnel;
  return (
    <Card>
      <Grid cols={4}>
        <Metric label="Active (7d)"      value={num(data.activeUsersSevenDays)} />
        <Metric label="Active (30d)"     value={num(data.activeUsersThirtyDays)} />
        <Metric label="Replies gen (7d)" value={num(data.repliesGeneratedSeven)} />
        <Metric label="Replies posted"   value={num(data.repliesPostedSeven)} />
      </Grid>
      <div style={dividerStyle} />
      <div style={subheaderStyle}>Onboarding funnel (30d)</div>
      {funnel.available ? (
        <>
          <Grid cols={4}>
            <Metric label="Installs started"    value={num(funnel.installsStarted)} />
            <Metric label="Installs completed"  value={num(funnel.installsCompleted)}
                    sub={funnel.completionRatePct != null ? `${funnel.completionRatePct}% completion` : undefined} />
            <Metric label="First review"        value={num(funnel.firstReviewCount)} />
            <Metric label="First reply"         value={num(funnel.firstReplyCount)} />
          </Grid>
          <div style={dividerStyle} />
          <KV k="Google connect taps"        v={num(funnel.googleConnectTapsTotal)} />
          <KV k="… of which mocked/blocked"  v={num(funnel.googleConnectMockedOrBlockedTotal)} />
        </>
      ) : (
        <div style={emptyStyle}>{funnel.reason}</div>
      )}
    </Card>
  );
}

function ReleaseHealth({ data }: { data: MetricsPayload['releaseHealth'] }) {
  if (!data.available) return <Card><div style={emptyStyle}>{data.reason}</div></Card>;
  return (
    <Card>
      {data.currentBuild ? (
        <Grid cols={3}>
          <Metric
            label="Current build"
            value={`${data.currentBuild.appVersion} (${data.currentBuild.buildNumber})`}
            sub={`${data.currentBuild.platform}${data.currentBuild.previewBuild ? ' · preview' : ''}`}
          />
          <Metric label="Users on build" value={num(data.currentBuild.distinctUsers)} sub={`last seen ${relativeTime(data.currentBuild.lastSeenAt)}`} />
          <Metric label="Users seen (30d)" value={num(data.totalUsersSeen)} />
        </Grid>
      ) : null}
      <div style={dividerStyle} />
      <div style={subheaderStyle}>Recent builds</div>
      <table style={tableStyle}>
        <thead><tr>
          <th style={thStyle}>Version</th>
          <th style={thStyle}>Platform</th>
          <th style={thStyle}>Users</th>
          <th style={thStyle}>Events</th>
          <th style={thStyle}>Last seen</th>
        </tr></thead>
        <tbody>
          {data.versions.map((v) => (
            <tr key={`${v.appVersion}-${v.buildNumber}-${v.platform}`}>
              <td style={tdStyle}>{v.appVersion} ({v.buildNumber})</td>
              <td style={tdStyle}>{v.platform}{v.previewBuild ? ' · prev' : ''}</td>
              <td style={tdStyle}>{num(v.distinctUsers)}</td>
              <td style={tdStyle}>{num(v.eventCount)}</td>
              <td style={tdStyle}>{relativeTime(v.lastSeenAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function Localization({ data }: { data: MetricsPayload['localization'] }) {
  if (!data.available) return <Card><div style={emptyStyle}>{data.reason}</div></Card>;
  return (
    <Card>
      <Grid cols={3}>
        <Metric label="Fallbacks (30d)"    value={num(data.fallbackTotal)} />
        <Metric label="Missing keys (30d)" value={num(data.missingTotal)}
                color={data.missingTotal > 0 ? COLORS.warn : undefined} />
        <Metric label="Users affected"     value={num(data.affectedUsers)} />
      </Grid>
      {data.fallbackByLang.length > 0 && (
        <>
          <div style={dividerStyle} />
          <div style={subheaderStyle}>Fallbacks by language</div>
          {data.fallbackByLang.map((r) => <KV key={r.label} k={r.label} v={num(r.count)} />)}
        </>
      )}
      {data.topMissingKeys.length > 0 && (
        <>
          <div style={dividerStyle} />
          <div style={subheaderStyle}>Missing keys (English absent)</div>
          {data.topMissingKeys.map((r) => (
            <KV key={r.label} k={r.label} v={num(r.count)} color={COLORS.critical} />
          ))}
        </>
      )}
    </Card>
  );
}

function CrashCenter({ data }: { data: MetricsPayload['crashCenter'] }) {
  return (
    <Card>
      <KV k="Client crash reporting"
          v={data.clientConfigured ? 'armed' : 'not configured'}
          color={data.clientConfigured ? COLORS.success : COLORS.warn} />
      <KV k="Discover API (server pull)"
          v={data.discoverReady ? 'ready' : 'not wired'}
          color={data.discoverReady ? COLORS.success : COLORS.textMuted} />
      {(data.org || data.project) && (
        <KV k="Project" v={`${data.org ?? '–'} / ${data.project ?? '–'}`} />
      )}
      <div style={{ ...emptyStyle, marginTop: 12 }}>{data.reason}</div>
    </Card>
  );
}

// ── Executive Snapshot ─────────────────────────────────────────────────────
function SectionExecutiveSnapshot({ briefs }: { briefs: ExecutiveAgentBrief[] }) {
  return (
    <div style={{ marginBottom: 32 }}>
      <div style={snapshotHeader}>
        <div>
          <h2 style={snapshotTitleStyle}>Executive Snapshot</h2>
          <div style={snapshotSubtitleStyle}>
            Your virtual ReVloom leadership team, updated whenever this dashboard refreshes.
          </div>
        </div>
      </div>
      <div style={agentGridStyle}>
        {briefs.map((b) => <AgentCard key={b.id} brief={b} />)}
      </div>
    </div>
  );
}

function AgentCard({ brief }: { brief: ExecutiveAgentBrief }) {
  const accent = severityColor(brief.severity);
  return (
    <div style={{ ...agentCardStyle, borderColor: accent + '55' }}>
      <div style={agentAccentBar(accent)} />
      <div style={agentHeaderRow}>
        <div>
          <div style={agentNameStyle}>{brief.name}</div>
          <div style={agentTitleStyle}>{brief.title}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <SeverityBadge severity={brief.severity} label={brief.severityLabel} />
          {brief.score != null && (
            <div style={{ ...agentScoreStyle, color: accent }}>{brief.score}</div>
          )}
        </div>
      </div>
      <div style={agentSummaryStyle}>{brief.summary}</div>
      <div style={agentFieldStyle}>
        <div style={agentFieldLabel}>Biggest risk</div>
        <div style={agentFieldValue}>{brief.risk}</div>
      </div>
      <div style={agentFieldStyle}>
        <div style={agentFieldLabel}>Recommended action</div>
        <div style={agentFieldValue}>{brief.action}</div>
      </div>
      {brief.evidence.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={agentFieldLabel}>Evidence</div>
          <ul style={agentEvidenceListStyle}>
            {brief.evidence.map((e, i) => (
              <li key={i} style={agentEvidenceItemStyle}>
                <span style={agentEvidenceDotStyle}>•</span>
                {e}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div style={agentFooterStyle}>Confidence · {brief.confidencePct}%</div>
    </div>
  );
}

function SeverityBadge({ severity, label }: { severity: AgentSeverity; label: string }) {
  const c = severityColor(severity);
  return (
    <span style={{
      display: 'inline-block',
      padding: '3px 10px', borderRadius: RADIUS.chip,
      background: c + '1a',
      color: c,
      border: `1px solid ${c}55`,
      fontSize: 11, fontWeight: 600, letterSpacing: 0.3,
    }}>{label}</span>
  );
}

function severityColor(severity: AgentSeverity): string {
  switch (severity) {
    case 'healthy':         return COLORS.success;
    case 'watch':           return COLORS.warn;
    case 'needs_attention': return '#F78B4B';  // orange, distinct from warn/critical
    case 'critical':        return COLORS.critical;
    case 'no_data':         return COLORS.textMuted;
  }
}

const snapshotHeader: React.CSSProperties = { marginBottom: 16 };
const snapshotTitleStyle: React.CSSProperties = {
  fontSize: 18, fontWeight: 600, margin: 0, color: COLORS.text, letterSpacing: -0.2,
};
const snapshotSubtitleStyle: React.CSSProperties = {
  color: COLORS.textMuted, fontSize: 12, marginTop: 4, lineHeight: 1.5,
};
const agentGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  gap: 16,
};
const agentCardStyle: React.CSSProperties = {
  position: 'relative', overflow: 'hidden',
  background: COLORS.surface, borderRadius: RADIUS.card,
  border: `1px solid ${COLORS.border}`,
  padding: '18px 18px 14px 18px',
  display: 'flex', flexDirection: 'column',
};
function agentAccentBar(color: string): React.CSSProperties {
  return {
    position: 'absolute', top: 0, left: 0, width: 3, height: '100%',
    background: color,
  };
}
const agentHeaderRow: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
  marginBottom: 12,
};
const agentNameStyle: React.CSSProperties = {
  color: COLORS.text, fontSize: 15, fontWeight: 600, letterSpacing: -0.1,
};
const agentTitleStyle: React.CSSProperties = {
  color: COLORS.textMuted, fontSize: 11, letterSpacing: 0.5,
  textTransform: 'uppercase', fontWeight: 600, marginTop: 2,
};
const agentScoreStyle: React.CSSProperties = {
  fontSize: 22, fontWeight: 600, marginTop: 6, letterSpacing: -0.4,
};
const agentSummaryStyle: React.CSSProperties = {
  color: COLORS.text, fontSize: 13, lineHeight: 1.5, marginBottom: 12,
};
const agentFieldStyle: React.CSSProperties = { marginBottom: 10 };
const agentFieldLabel: React.CSSProperties = {
  color: COLORS.textMuted, fontSize: 10, letterSpacing: 0.6,
  textTransform: 'uppercase', fontWeight: 600, marginBottom: 3,
};
const agentFieldValue: React.CSSProperties = {
  color: COLORS.textSecondary, fontSize: 12, lineHeight: 1.5,
};
const agentEvidenceListStyle: React.CSSProperties = {
  margin: 0, padding: 0, listStyle: 'none',
};
const agentEvidenceItemStyle: React.CSSProperties = {
  color: COLORS.textMuted, fontSize: 11, lineHeight: 1.5,
  marginBottom: 3, display: 'flex', gap: 6,
};
const agentEvidenceDotStyle: React.CSSProperties = {
  color: COLORS.textMuted, flexShrink: 0, opacity: 0.7,
};
const agentFooterStyle: React.CSSProperties = {
  marginTop: 12, paddingTop: 10, borderTop: `1px solid ${COLORS.border}`,
  color: COLORS.textMuted, fontSize: 10, letterSpacing: 0.5, textTransform: 'uppercase',
};

const loadingStyle: React.CSSProperties = { padding: 40, color: COLORS.textMuted };
const errorStyle:   React.CSSProperties = { padding: 40, color: COLORS.critical, display: 'flex', alignItems: 'center', gap: 12 };
const dividerStyle: React.CSSProperties = { height: 1, background: COLORS.border, margin: '16px 0' };
const subheaderStyle: React.CSSProperties = {
  color: COLORS.textMuted, fontSize: 11, letterSpacing: 0.6, textTransform: 'uppercase',
  fontWeight: 600, marginBottom: 8,
};
const emptyStyle:   React.CSSProperties = { color: COLORS.textMuted, fontSize: 12, lineHeight: 1.6 };
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '8px 0', color: COLORS.textMuted, fontSize: 11,
  letterSpacing: 0.5, textTransform: 'uppercase', borderBottom: `1px solid ${COLORS.border}`,
};
const tdStyle: React.CSSProperties = { padding: '10px 0', borderBottom: `1px dashed ${COLORS.border}`, color: COLORS.text };
// Kept for future — Pill used by callers of this module.
export { Pill };
